import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { api } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Icon } from '../components/icons/Icon';

interface Pack { id: string; sp: number; yen: number; label: string; }

export const SpPurchase = () => {
    const [location, setLocation] = useLocation();
    const isSuccess = location.startsWith('/sp/success');

    const [packs, setPacks] = useState<Pack[]>([]);
    const [configured, setConfigured] = useState(true);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);

    useEffect(() => {
        api.getSpPacks().then((d) => {
            setPacks(d.packs || []);
            setConfigured(!!d.configured);
        }).finally(() => setLoading(false));
    }, []);

    const buy = async (packId: string) => {
        setBusy(packId);
        const res = await api.createSpCheckout(packId);
        if (res.success && res.url) {
            window.location.href = res.url; // Stripe Checkout へ遷移
            return;
        }
        if (res.code === 'UNAUTHORIZED') {
            toast({ title: 'ログインが必要です', description: '右上のログインからどうぞ' });
        } else if (res.code === 'STRIPE_NOT_CONFIGURED') {
            toast({ title: '現在ご利用いただけません', description: '決済の準備中です' });
            setConfigured(false);
        } else {
            toast({ title: '購入を開始できませんでした', description: res.message });
        }
        setBusy(null);
    };

    if (isSuccess) {
        const sp = new URLSearchParams(window.location.search).get('sp');
        return (
            <div className="max-w-md mx-auto px-6 pt-16 pb-24 text-center">
                <div className="pop-card p-8">
                    <span className="w-14 h-14 rounded-full border-2 border-ink bg-sun text-ink flex items-center justify-center mx-auto mb-4">
                        <Icon name="check" size={28} />
                    </span>
                    <h1 className="font-display font-black text-2xl text-ink mb-2">購入ありがとうございます！</h1>
                    <p className="text-ink-2 text-sm mb-6">
                        {sp ? `${Number(sp).toLocaleString()} SP` : 'SP'} の決済が完了しました。残高への反映まで少し時間がかかる場合があります。
                    </p>
                    <button className="btn btn-primary" onClick={() => setLocation('/')}>
                        ホームに戻る <Icon name="arrow-right" size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-24">
            <div className="text-center mb-8">
                <p className="font-display font-bold tracking-[0.18em] text-pop text-sm mb-2">SP STORE</p>
                <h1 className="font-display font-black text-4xl text-ink">SPを購入</h1>
                <p className="text-ink-2 text-sm mt-2">SP（サポートポイント）はギフトなどで使えます。</p>
            </div>

            {loading ? (
                <div className="py-16 text-center text-primary font-display font-bold animate-pulse">読み込み中...</div>
            ) : !configured ? (
                <div className="pop-card p-8 text-center">
                    <span className="w-12 h-12 rounded-full border-2 border-ink bg-cream-2 text-ink flex items-center justify-center mx-auto mb-3">
                        <Icon name="lock" size={22} />
                    </span>
                    <p className="font-display font-bold text-ink mb-1">準備中です</p>
                    <p className="text-ink-3 text-sm">SPの購入機能は現在準備中です。もうしばらくお待ちください。</p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-3 gap-4">
                    {packs.map((p) => (
                        <div key={p.id} className="pop-card p-5 flex flex-col items-center text-center">
                            <span className="w-12 h-12 rounded-full border-2 border-ink bg-pop text-white flex items-center justify-center mb-3">
                                <Icon name="gift" size={22} />
                            </span>
                            <div className="font-display font-black text-2xl text-ink">{p.sp.toLocaleString()}<span className="text-sm font-bold ml-1">SP</span></div>
                            <div className="text-ink-2 text-sm mt-1 mb-4">¥{p.yen.toLocaleString()}</div>
                            <button
                                className="btn btn-primary btn-sm w-full"
                                disabled={busy === p.id}
                                onClick={() => buy(p.id)}
                            >
                                {busy === p.id ? '処理中...' : '購入する'}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-center text-ink-3 text-xs mt-8">
                決済は Stripe を通じて安全に処理されます。SPは作品への応援（ギフト）にご利用いただけます。
            </p>
        </div>
    );
};
